import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField, DialogVariable } from '@/lib/stores/dialogStore';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useFieldEditingHandlers, FieldTextInput } from './BaseFieldEditor';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ConnectionBadge } from './ConnectionBadge';

export interface SetterFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentSectionFields?: DialogField[];
  currentDialogId?: string;
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  gameId?: string;
  t: any;
}

/**
 * SetterFieldEditor handles 'setter' field type
 * Allows setting, incrementing, or decrementing variable values
 */
export const SetterFieldEditor = React.memo(function SetterFieldEditor({
  field,
  fieldIndex,
  sectionIndex,
  sectionIdentifiers,
  sections,
  currentSectionFields,
  currentDialogId,
  dialogTranslatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  onUpdateField,
  onRemoveField,
  gameId,
  t,
}: SetterFieldEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  const { locaKey, displayText, sourceText, isTranslationMode, handleTextChange, handleTextBlur } = useFieldEditingHandlers(
    field, effectiveGameId, onUpdateField, sectionIndex
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Get selected variable details
  const selectedVariable = dialogVariables?.find(v => v.name === field.setter?.variable);
  const variableType = selectedVariable?.type;
  const isBoolean = variableType === 'boolean';
  const isText = variableType === 'text';
  const isRange = variableType === 'range';
  const isNumber = variableType === 'number' || isRange;


  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 border border-gray-200 rounded-sm bg-white"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-2"
        type="button"
      >
        <IconProvider iconstring="fa-grip-vertical" size={14} />
      </button>

      {/* Field Content */}
      <div className="flex-1 items-center justify-center space-y-2">
        {/* Field Text with Translation Icon */}
        <FieldTextInput
          field={field}
          displayText={displayText}
          locaKey={locaKey}
          sourceText={sourceText}
          isTranslationMode={isTranslationMode}
          dialogTranslatable={dialogTranslatable}
          onTextChange={handleTextChange}
          onTextBlur={handleTextBlur}
          onTypeChange={(type) => {
            const updates: Partial<DialogField> = { type };
            // Initialize setter object if changing to setter type and it doesn't exist
            if (type === 'setter' && !field.setter) {
              updates.setter = {
                variable: '',
                operator: 'set',
                value: 0
              };
            }
            onUpdateField(sectionIndex, field.id, updates);
          }}
          placeholder="Enter setter description (optional)..."
          gameId={effectiveGameId}
        />

        {/* Setter Configuration */}
        <div className="space-y-3 bg-green-50 p-3 rounded border border-green-200">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <IconProvider iconstring="fa-pen-to-square" size={14} />
            Variable Setter Configuration
          </Label>

          {/* Variable Selection */}
          <div className="space-y-2 bg-white p-2 rounded border border-green-300">
            <Label className="text-xs text-gray-600">Variable to set:</Label>
            {dialogVariables && dialogVariables.filter(v => v.name.trim()).length > 0 ? (
              <Select
                value={field.setter?.variable || ''}
                onValueChange={(value) => {
                  const selectedVar = dialogVariables.find(v => v.name === value);
                  const defaultValue = selectedVar?.type === 'boolean' ? false : 
                                     selectedVar?.type === 'text' ? '' : 0;
                  onUpdateField(sectionIndex, field.id, {
                    setter: {
                      variable: value,
                      operator: 'set',
                      value: defaultValue
                    }
                  });
                }}
              >
                <SelectTrigger className="h-8 text-sm font-mono">
                  <SelectValue placeholder="Select variable..." />
                </SelectTrigger>
                <SelectContent>
                  {dialogVariables.filter(v => v.name.trim()).map((v) => (
                    <SelectItem key={v.name} value={v.name}>
                      {v.name} ({v.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={field.setter?.variable || ''}
                onChange={(e) =>
                  onUpdateField(sectionIndex, field.id, {
                    setter: {
                      variable: e.target.value,
                      operator: field.setter?.operator || 'set',
                      value: field.setter?.value ?? 0
                    }
                  })
                }
                placeholder="variable_name"
                className="h-8 text-sm font-mono"
              />
            )}
          </div>

          {/* Operation and Value Configuration */}
          {field.setter?.variable && (
            <div className="space-y-2 bg-white p-2 rounded border border-green-300">
              <Label className="text-xs text-gray-600">Operation and Value:</Label>
              <div className="grid gap-2" style={{ gridTemplateColumns: isText || isBoolean ? '1fr' : '1fr 2fr' }}>
                {/* Operator Selector (hidden for text and boolean) */}
                {!isText && !isBoolean && (
                  <Select
                    value={field.setter?.operator || 'set'}
                    onValueChange={(value) =>
                      onUpdateField(sectionIndex, field.id, {
                        setter: {
                          variable: field.setter?.variable || '',
                          value: field.setter?.value ?? 0,
                          operator: value as 'set' | 'increment' | 'decrement'
                        }
                      })
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="increment">Increment</SelectItem>
                      <SelectItem value="decrement">Decrement</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                
                {/* Value Input - varies by type */}
                {isBoolean ? (
                  <Select
                    value={String(field.setter?.value ?? false)}
                    onValueChange={(value) =>
                      onUpdateField(sectionIndex, field.id, {
                        setter: {
                          variable: field.setter?.variable || '',
                          operator: field.setter?.operator || 'set',
                          value: value === 'true'
                        }
                      })
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : isText ? (
                  <Input
                    type="text"
                    value={String(field.setter?.value ?? '')}
                    onChange={(e) =>
                      onUpdateField(sectionIndex, field.id, {
                        setter: {
                          variable: field.setter?.variable || '',
                          operator: field.setter?.operator || 'set',
                          value: e.target.value
                        }
                      })
                    }
                    placeholder="text value"
                    className="h-8 text-sm font-mono"
                  />
                ) : (
                  <Input
                    type="number"
                    value={field.setter?.value !== undefined ? String(field.setter.value) : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const parsedValue = value === '' ? 0 : parseFloat(value);
                      onUpdateField(sectionIndex, field.id, {
                        setter: {
                          variable: field.setter?.variable || '',
                          operator: field.setter?.operator || 'set',
                          value: isNaN(parsedValue) ? 0 : parsedValue
                        }
                      });
                    }}
                    placeholder={isRange ? `${selectedVariable?.rangeMin ?? 0}-${selectedVariable?.rangeMax ?? 100}` : "value"}
                    className="h-8 text-sm font-mono"
                    min={isRange ? selectedVariable?.rangeMin : undefined}
                    max={isRange ? selectedVariable?.rangeMax : undefined}
                    step={isRange ? selectedVariable?.increment : undefined}
                  />
                )}
              </div>
              
              {/* Range info */}
              {isRange && selectedVariable && (
                <div className="text-xs text-gray-500 italic">
                  Range: {selectedVariable.rangeMin ?? 0} to {selectedVariable.rangeMax ?? 100}, step: {selectedVariable.increment ?? 1}
                </div>
              )}

              {/* Operation hint */}
              {!isText && !isBoolean && field.setter?.operator !== 'set' && (
                <div className="text-xs text-gray-600 italic">
                  {field.setter?.operator === 'increment' && `Will add ${field.setter?.value} to ${field.setter?.variable}`}
                  {field.setter?.operator === 'decrement' && `Will subtract ${field.setter?.value} from ${field.setter?.variable}`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Field Type & Buttons */}
      <div className="flex items-center gap-1">
        {/* Connection Badge */}
        <ConnectionBadge
          field={field}
          sectionIndex={sectionIndex}
          sectionIdentifiers={sectionIdentifiers}
          sections={sections}
          currentSectionFields={currentSectionFields}
          currentDialogId={currentDialogId}
          effectiveGameId={effectiveGameId}
          onUpdateField={onUpdateField}
          showFieldOption={true}
        />
        {/* Remove Field Button */}
        <Button
          variant="halfghost"
          size="sm"
          onClick={() => onRemoveField(sectionIndex, field.id)}
          className="px-2 text-xs w-8 h-8"
          type="button"
        >
          <IconProvider
            iconstring="fa-xmark"
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      </div>
    </div>
  );
});
